import React, { createContext, useContext, useMemo } from 'react'
import { createContextualCan } from '@casl/react'
import { Ability, detectSubjectType } from '@casl/ability'
import { gql } from '@apollo/client'
import { currentLocale } from 'i18n'
import { useCurrentContextQuery, CurrentContextQuery, TimeFormat } from 'schema'

import Loading from 'components/loading'
import { Error } from 'components/error'
import { compact, map, uniq } from 'lodash'

const REFRESH_INTERVAL_MS = 10 * 60 * 1000

gql`
  query CurrentContext {
    abilities {
      action
      subject
      fields
      conditions
      inverted
    }

    currentUser {
      id
      fullName
      firstName
      profilePictureUrl
      userCredential {
        id
        email
      }
      backgroundPictureVariant
      backgroundPictureUrl
      iconPack
      positionName
      teams {
        id
        name
      }
    }

    currentAccount {
      id
      locale
      otherLocales
      timeFormat
      dateFormat
      allowedTcCreationTime
      firstApprover
      featureFlags
    }
  }
`

interface GraphQLObject {
  __typename?: 'string'
}

const graphQLSubjectName = (object: GraphQLObject): string => object.__typename ?? detectSubjectType(object)

const AbilityContext = createContext<Ability>(new Ability([]))

const CurrentUserContext = createContext<CurrentContextQuery['currentUser']>({ id: '', firstName: '', fullName: '', teams: [] })

const defaultCurrentAccount = {
  id: '',
  locale: currentLocale,
  otherLocales: [],
  allLocales: [currentLocale],
  timeFormat: TimeFormat.Decimal,
  dateFormat: 'dd.MM.yyyy',
  dateFormatWithTime: 'dd.MM.yyyy HH:mm'
}

const CurrentAccountContext = createContext<CurrentContextQuery['currentAccount'] & { allLocales: string[]; dateFormatWithTime: string }>(
  defaultCurrentAccount
)

export const useAbility = () => useContext(AbilityContext)

export const useCurrentUser = () => useContext(CurrentUserContext)

export const useCurrentAccount = () => useContext(CurrentAccountContext)

// `feature_flags` is a free-text account setting (edited in Admin → System) that can
// mix two forms, space/comma separated:
//   - a bare token, e.g. `ask_ai`, enabling the flag for every user on the account
//   - a JSON object mapping a flag name to an email allowlist, e.g.
//     {"ask_ai": ["jane@firm.com", "bob@firm.com"]}, enabling it only for those users
// Mirrors Account#feature_flag? on the backend.
export const useFeatureFlag = (flag: string): boolean => {
  const { featureFlags } = useCurrentAccount()
  const email = useCurrentUser().userCredential?.email

  return useMemo(() => {
    const raw = featureFlags ?? ''
    const tokens = raw
      .replace(/\{[^{}]*\}/g, '')
      .split(/[\s,]+/)
      .filter(Boolean)

    if (tokens.includes(flag)) {
      return true
    }

    if (!email) {
      return false
    }

    const segments = raw.match(/\{[^{}]*\}/g) ?? []

    return segments.some((segment) => {
      try {
        const allowedEmails: string[] = JSON.parse(segment)[flag] ?? []
        return allowedEmails.some((allowedEmail) => allowedEmail.toLowerCase() === email.toLowerCase())
      } catch {
        return false
      }
    })
  }, [featureFlags, flag, email])
}

export const CurrentContext: React.FC = ({ children }) => {
  const { loading, error, data } = useCurrentContextQuery({ pollInterval: REFRESH_INTERVAL_MS })

  const currentAccount = useMemo(() => {
    const { currentAccount } = data || { currentAccount: defaultCurrentAccount }

    const allLocales = uniq(compact([currentAccount.locale, ...(currentAccount.otherLocales || [])]))

    const dateFormatWithTime = `${currentAccount.dateFormat} HH:mm`

    return { ...currentAccount, allLocales, dateFormatWithTime }
  }, [data])

  if (loading) {
    return <Loading fullScreen />
  }

  if (error || !data) {
    return <Error error={error} />
  }

  const abilities = map(data.abilities, ({ fields, ...other }) => ({ ...other, fields: fields || undefined }))

  return (
    <CurrentUserContext.Provider value={data.currentUser}>
      <CurrentAccountContext.Provider value={currentAccount}>
        <AbilityContext.Provider value={new Ability(abilities, { detectSubjectType: graphQLSubjectName })}>{children}</AbilityContext.Provider>
      </CurrentAccountContext.Provider>
    </CurrentUserContext.Provider>
  )
}

export const Can = createContextualCan(AbilityContext.Consumer)
